import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"
import { HugeiconsIcon, Icons } from "@/components/icons"
import { cn } from "@/lib/utils"

export const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-2xl app-text-title-small transition-colors overflow-hidden focus-visible:outline-none disabled:pointer-events-none relative",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground hover:bg-primary-75 disabled:bg-alternate",
        destructive: "bg-error text-light-background hover:bg-error/90",
        outline:
          "border border-primary text-primary bg-background hover:bg-primary-25 disabled:border-alternate disabled:text-alternate disabled:bg-secondary-background",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "text-primary disabled:text-alternate",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 px-3",
        lg: "h-11 px-8",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
  isLoading?: boolean
  loadingIcon?: React.ReactNode
  startContent?: React.ReactNode
  endContent?: React.ReactNode
  fullWidth?: boolean
  showRipple?: boolean
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      isLoading = false,
      disabled,
      children,
      loadingIcon,
      startContent,
      endContent,
      fullWidth = false,
      showRipple = true,
      onClick,
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot : "button"
    const buttonRef = React.useRef<HTMLButtonElement>(null)

    const createRipple = React.useCallback(
      (event: React.MouseEvent<HTMLButtonElement>) => {
        // Always call onClick first
        if (onClick) {
          onClick(event)
        }

        // Only create ripple if enabled
        if (!showRipple) {
          return
        }

        // For asChild, we can't reliably get the ref, so skip ripple
        if (asChild) {
          return
        }

        const button = buttonRef.current
        if (!button) {
          return
        }

        // Get button position
        const rect = button.getBoundingClientRect()
        const buttonWidth = button.offsetWidth
        const buttonHeight = button.offsetHeight

        // Calculate ripple size (largest dimension)
        const rippleSize = Math.max(buttonWidth, buttonHeight)
        const radius = rippleSize / 2

        // Get click position relative to button
        const x = event.clientX - rect.left
        const y = event.clientY - rect.top

        // Create ripple element
        const ripple = document.createElement("span")

        // Set initial styles
        ripple.style.cssText = `
          position: absolute;
          width: ${rippleSize}px;
          height: ${rippleSize}px;
          left: ${x - radius}px;
          top: ${y - radius}px;
          border-radius: 50%;
          background-color: rgba(255, 255, 255, 0.6);
          pointer-events: none;
          transform: scale(0);
          opacity: 0.6;
          transition: transform 0.6s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.6s cubic-bezier(0.4, 0, 0.2, 1);
          z-index: 10;
        `

        // Remove existing ripples
        const existingRipples = button.querySelectorAll(".button-ripple")
        existingRipples.forEach((r) => r.remove())

        ripple.classList.add("button-ripple")
        button.appendChild(ripple)

        // Force reflow to ensure initial state is applied
        void ripple.offsetWidth

        // Trigger animation in next frame
        requestAnimationFrame(() => {
          ripple.style.transform = "scale(2.5)"
          ripple.style.opacity = "0"
        })

        // Clean up after animation completes
        setTimeout(() => {
          if (ripple.parentNode === button) {
            ripple.remove()
          }
        }, 600)
      },
      [onClick, showRipple, asChild]
    )

    const defaultLoadingIcon = (
      <HugeiconsIcon
        icon={Icons.Loading02}
        size={16}
        className="animate-spin"
      />
    )

    return (
      <Comp
        type="button"
        className={cn(
          buttonVariants({ variant, size, className }),
          fullWidth && "w-full"
        )}
        ref={(node) => {
          if (typeof ref === "function") {
            ref(node)
          } else if (ref) {
            ;(ref as React.MutableRefObject<HTMLButtonElement | null>).current =
              node
          }
          buttonRef.current = node
        }}
        disabled={disabled || isLoading}
        onClick={createRipple}
        {...props}
      >
        {isLoading && (loadingIcon || defaultLoadingIcon)}
        {!isLoading && startContent}
        {children}
        {endContent}
      </Comp>
    )
  }
)
Button.displayName = "Button"
